import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import dataService, { fetchLessons } from './services/dataService';
import Login from './components/Login';
import Register from './components/Register';
import Header from './components/Header';
import LessonList from './components/LessonList';
import LessonDetail from './components/LessonDetail';
import PhrasePractice from './components/PhrasePractice';
import Footer from './components/Footer';
import ExerciseGenerator from './components/ExerciseGenerator';
import HangmanGame from './components/HangmanGame';
import WordScrambleGame from './components/WordScrambleGame';
import DragDropGame from './components/DragDropGame';
import Chatbot from './components/Chatbot';
import AdminPanel from './components/AdminPanel';

// Component bảo vệ route admin
const ProtectedAdminRoute = ({ children, currentUser }) => {
  if (!currentUser || currentUser.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  return children;
};

function EnglishLearningApp() {
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newLessonTitle, setNewLessonTitle] = useState('');
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [newPhrase, setNewPhrase] = useState('');
  const [fadeIn, setFadeIn] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastAdded, setLastAdded] = useState(null);
  const [isTVMode, setIsTVMode] = useState(false);

  const [enlargedPhrase, setEnlargedPhrase] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioURL, setAudioURL] = useState('');
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcription, setTranscription] = useState('');
  const [transcriptionFeedback, setTranscriptionFeedback] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionAccuracy, setTranscriptionAccuracy] = useState(null);
  const [view, setView] = useState('lessons'); // 'lessons', 'exercise-generator', 'hangman', 'word-scramble', 'chatbot'

  const [isAddPhraseRecording, setIsAddPhraseRecording] = useState(false);
  const [addPhraseAudioURL, setAddPhraseAudioURL] = useState('');
  const [addPhraseRecordingTime, setAddPhraseRecordingTime] = useState(0);
  const [addPhraseAudioBlob, setAddPhraseAudioBlob] = useState(null);
  const [isAddPhraseTranscribing, setIsAddPhraseTranscribing] = useState(false);

  // State cho phân trang cụm từ
  const [currentPage, setCurrentPage] = useState(1);
  const [phrasesPerPage] = useState(6); // Hiển thị 6 cụm từ mỗi trang (3 cụm từ mỗi cột)

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  const addPhraseMediaRecorderRef = useRef(null);
  const addPhraseAudioChunksRef = useRef([]);
  const addPhraseTimerRef = useRef(null);

  useEffect(() => {
    const checkAuth = () => {
      const isAuth = dataService.isAuthenticated();
      setIsAuthenticated(isAuth);

      if (isAuth) {
        const user = dataService.getCurrentUser();
        setCurrentUser(user);
        loadLessons();
      }

      setAuthChecked(true);
    };

    // Clear any old sample data from localStorage
    localStorage.removeItem('english_lessons');

    checkAuth();

    // Phát hiện TV mode (màn hình >= 2560px - 4K TV)
    const checkTVMode = () => {
      setIsTVMode(window.innerWidth >= 2560);
    };

    checkTVMode();
    window.addEventListener('resize', checkTVMode);

    return () => window.removeEventListener('resize', checkTVMode);
  }, []);

  const handleLoginSuccess = (user) => {
    setIsAuthenticated(true);
    setCurrentUser(user);
    setShowLoginForm(false);
    loadLessons();
  };

  const handleRegisterSuccess = () => {
    setShowRegisterForm(false);
    setShowLoginForm(true);
  };

  const handleLogout = () => {
    dataService.logoutUser();
    setIsAuthenticated(false);
    setCurrentUser(null);
    setLessons([]);
    setView('lessons');
  };

  const loadLessons = async () => {
    setIsLoading(true);
    try {
      const data = await fetchLessons();
      if (data === null) {
        const localData = localStorage.getItem('english_lessons');
        if (localData) {
          setLessons(JSON.parse(localData));
        } else {
          setLessons([]); // Không có dữ liệu mẫu, bắt đầu với danh sách trống
        }
        setError('Không thể kết nối đến server, đang sử dụng dữ liệu cục bộ');
      } else {
        setLessons(data);
        setError(null);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Đã xảy ra lỗi khi tải dữ liệu');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadLessons();
    }

    setTimeout(() => {
      setFadeIn(true);
    }, 100);

    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (addPhraseMediaRecorderRef.current && addPhraseMediaRecorderRef.current.state === 'recording') {
        addPhraseMediaRecorderRef.current.stop();
      }
      if (addPhraseTimerRef.current) {
        clearInterval(addPhraseTimerRef.current);
      }
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && enlargedPhrase !== null) {
        closeEnlargedView();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enlargedPhrase]);

  useEffect(() => {
    if (transcriptionAccuracy !== null) {
      playAccuracySound(transcriptionAccuracy);
    }
  }, [transcriptionAccuracy]);

  // Reset trang về 1 khi chọn bài học mới
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedLesson]);

  const addLesson = async () => {
    if (newLessonTitle.trim() === '') return;
    try {
      const newLesson = await dataService.createLesson(newLessonTitle);
      if (newLesson) {
        setLessons([...lessons, { ...newLesson, phrases: [] }]); // Ensure phrases array exists
        setNewLessonTitle('');
        setLastAdded(newLesson.id);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      }
    } catch (error) {
      setError(error.message);
      console.error('Failed to add lesson:', error);
    }
  };

  const selectLesson = (lesson) => {
    setSelectedLesson(lesson);
    setNewPhrase('');
    resetAddPhraseRecording();
  };

  const addPhrase = async () => {
    if (newPhrase.trim() === '' || !selectedLesson) return;

    try {
      const addedPhrase = await dataService.addPhrase(selectedLesson.id, newPhrase);
      
      if (addedPhrase) {
        const updatedPhrases = [...selectedLesson.phrases, addedPhrase];
        const updatedLesson = { ...selectedLesson, phrases: updatedPhrases };

        const updatedLessons = lessons.map(l =>
          l.id === selectedLesson.id ? updatedLesson : l
        );

        setLessons(updatedLessons);
        setSelectedLesson(updatedLesson);
        setNewPhrase('');
        resetAddPhraseRecording();
        
        setShowSuccess('Cụm từ đã được thêm!');
        setTimeout(() => setShowSuccess(false), 2000);
      }
    } catch (error) {
      setError(error.message);
      console.error('Failed to add phrase:', error);
    }
  };

  const deleteLesson = async (lessonId) => {
    try {
      const success = await dataService.deleteLesson(lessonId);
      if (success) {
        const updatedLessons = lessons.filter(lesson => lesson.id !== lessonId);
        setLessons(updatedLessons);
        // If the deleted lesson was the selected one, deselect it
        if (selectedLesson && selectedLesson.id === lessonId) {
          setSelectedLesson(null);
        }
      }
    } catch (error) {
      setError(error.message);
      console.error('Failed to delete lesson:', error);
    }
  };

  const deletePhrase = async (phraseId) => {
    if (!selectedLesson) return;

    try {
      const success = await dataService.deletePhrase(phraseId);
      if (success) {
        const updatedPhrases = selectedLesson.phrases.filter(p => p.id !== phraseId);
        const updatedLesson = { ...selectedLesson, phrases: updatedPhrases };

        const updatedLessons = lessons.map(l => 
          l.id === selectedLesson.id ? updatedLesson : l
        );

        setLessons(updatedLessons);
        setSelectedLesson(updatedLesson);
      }
    } catch (error) {
      setError(error.message);
      console.error('Failed to delete phrase:', error);
    }
  };

  const backToLessons = () => {
    setSelectedLesson(null);
    resetAddPhraseRecording();
  };

  const getGradient = (id) => {
    const gradients = [
      'bg-gradient-to-br from-blue-500 to-purple-600',
      'bg-gradient-to-br from-green-500 to-teal-600',
      'bg-gradient-to-br from-pink-500 to-rose-600',
      'bg-gradient-to-br from-yellow-500 to-orange-600',
      'bg-gradient-to-br from-indigo-500 to-blue-600',
      'bg-gradient-to-br from-purple-500 to-indigo-600',
      'bg-gradient-to-br from-red-500 to-pink-600',
      'bg-gradient-to-br from-orange-500 to-red-600',
      'bg-gradient-to-br from-teal-500 to-green-600',
      'bg-gradient-to-br from-cyan-500 to-blue-600'
    ];
    return gradients[id % gradients.length];
  };

  const mainGradient = 'bg-white';

  const showEnlargedPhrase = (phrase) => {
    // Normalize the phrase data for PhrasePractice component
    const { english, vietnamese } = splitPhrase(phrase);
    const normalizedPhrase = {
      text: english,
      translation: vietnamese,
      original: phrase // Keep original for reference
    };

    setEnlargedPhrase(normalizedPhrase);
    setAudioURL('');
    setIsRecording(false);
    setRecordingTime(0);
  };

  const closeEnlargedView = () => {
    setEnlargedPhrase(null);
    if (isRecording) {
      stopRecording();
    }
    if (audioURL) {
      URL.revokeObjectURL(audioURL);
      setAudioURL('');
    }
    setTranscription('');
    setTranscriptionAccuracy(null);
    setTranscriptionFeedback('');
    setAudioBlob(null);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const url = URL.createObjectURL(audioBlob);
        setAudioURL(url);
        setAudioBlob(audioBlob);
        setIsRecording(false);
        clearInterval(timerRef.current);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Không thể bắt đầu ghi âm:', err);
      alert('Không thể truy cập microphone. Vui lòng kiểm tra quyền truy cập.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const startAddPhraseRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      addPhraseMediaRecorderRef.current = new MediaRecorder(stream);
      addPhraseAudioChunksRef.current = [];

      addPhraseMediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          addPhraseAudioChunksRef.current.push(e.data);
        }
      };

      addPhraseMediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(addPhraseAudioChunksRef.current, { type: 'audio/wav' });
        const url = URL.createObjectURL(audioBlob);
        setAddPhraseAudioURL(url);
        setAddPhraseAudioBlob(audioBlob);
        setIsAddPhraseRecording(false);
        clearInterval(addPhraseTimerRef.current);

        // Tự động transcribe ngay khi dừng ghi âm
        setTimeout(() => {
          transcribeAddPhraseAudioAuto(audioBlob);
        }, 500); // Delay nhỏ để đảm bảo state đã được cập nhật
      };

      addPhraseMediaRecorderRef.current.start();
      setIsAddPhraseRecording(true);
      setAddPhraseRecordingTime(0);

      addPhraseTimerRef.current = setInterval(() => {
        setAddPhraseRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Không thể bắt đầu ghi âm:', err);
      alert('Không thể truy cập microphone. Vui lòng kiểm tra quyền truy cập.');
    }
  };

  const stopAddPhraseRecording = () => {
    if (addPhraseMediaRecorderRef.current && addPhraseMediaRecorderRef.current.state === 'recording') {
      addPhraseMediaRecorderRef.current.stop();
      addPhraseMediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    if (addPhraseTimerRef.current) {
      clearInterval(addPhraseTimerRef.current);
    }
  };

  const resetAddPhraseRecording = () => {
    if (isAddPhraseRecording) {
      stopAddPhraseRecording();
    }
    if (addPhraseAudioURL) {
      URL.revokeObjectURL(addPhraseAudioURL);
    }
    setIsAddPhraseRecording(false);
    setAddPhraseAudioURL('');
    setAddPhraseRecordingTime(0);
    setAddPhraseAudioBlob(null);
  };

  // Function tự động transcribe khi dừng ghi âm
  const transcribeAddPhraseAudioAuto = async (audioBlob) => {
    try {
      setIsAddPhraseTranscribing(true);

      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.wav');
      formData.append('language', 'en');

      const response = await fetch('http://192.168.1.5:7030/api/transcribe', {
        method: 'POST',
        headers: {
          'Accept': 'application/json'
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Lỗi chi tiết từ server:", errorText);
        throw new Error(`Lỗi khi gửi file ghi âm: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.full_text) {
        setNewPhrase(data.full_text);
      }

    } catch (error) {
      console.error('Lỗi khi chuyển đổi giọng nói:', error);
    } finally {
      setIsAddPhraseTranscribing(false);
    }
  };

  // Function thủ công transcribe (giữ lại cho trường hợp cần)
  const transcribeAddPhraseAudio = async () => {
    if (!addPhraseAudioBlob) return;

    try {
      setIsAddPhraseTranscribing(true);

      const formData = new FormData();
      formData.append('file', addPhraseAudioBlob, 'recording.wav');
      formData.append('language', 'en');

      const response = await fetch('http://192.168.1.5:7030/api/transcribe', {
        method: 'POST',
        headers: {
          'Accept': 'application/json'
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Lỗi chi tiết từ server:", errorText);
        throw new Error(`Lỗi khi gửi file ghi âm: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.full_text) {
        setNewPhrase(data.full_text);
      } else {
        alert('Không nhận diện được giọng nói');
      }

    } catch (error) {
      console.error('Lỗi khi chuyển đổi giọng nói:', error);
      alert('Đã xảy ra lỗi khi xử lý giọng nói. Vui lòng thử lại.');
    } finally {
      setIsAddPhraseTranscribing(false);
    }
  };

  const transcribeAudio = async () => {
    if (!audioBlob) return;

    try {
      setIsTranscribing(true);

      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.wav');
      formData.append('language', 'en');

      const response = await fetch('http://192.168.1.5:7030/api/transcribe', {
        method: 'POST',
        headers: {
          'Accept': 'application/json'
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Lỗi chi tiết từ server:", errorText);
        throw new Error(`Lỗi khi gửi file ghi âm: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setTranscription(data.full_text || 'Không nhận diện được giọng nói');

      if (data.full_text && enlargedPhrase) {
        const originalEnglish = enlargedPhrase.text.toLowerCase().trim();
        const transcriptText = data.full_text.toLowerCase().trim();

        const accuracy = calculateTextSimilarity(originalEnglish, transcriptText);
        setTranscriptionAccuracy(accuracy);

        if (accuracy > 80) {
          setTranscriptionFeedback('Tuyệt vời! Phát âm của bạn rất chính xác.');
        } else if (accuracy > 50) {
          setTranscriptionFeedback('Khá tốt! Tiếp tục luyện tập để cải thiện phát âm.');
        } else {
          setTranscriptionFeedback('Cần cải thiện. Hãy nghe lại và thử lại nhé!');
        }
      }

    } catch (error) {
      console.error('Lỗi khi chuyển đổi giọng nói:', error);
      setTranscription('Đã xảy ra lỗi khi xử lý giọng nói. Vui lòng thử lại.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const calculateTextSimilarity = (str1, str2) => {
    if (str1 === str2) return 100;
    if (str1.length === 0) return 0;
    if (str2.length === 0) return 0;

    const levenshteinDistance = (a, b) => {
      const matrix = Array(b.length + 1).fill().map(() => Array(a.length + 1).fill(0));

      for (let i = 0; i <= a.length; i++) {
        matrix[0][i] = i;
      }

      for (let j = 0; j <= b.length; j++) {
        matrix[j][0] = j;
      }

      for (let j = 1; j <= b.length; j++) {
        for (let i = 1; i <= a.length; i++) {
          const cost = a[i - 1] === b[j - 1] ? 0 : 1;
          matrix[j][i] = Math.min(
            matrix[j][i - 1] + 1,
            matrix[j - 1][i] + 1,
            matrix[j - 1][i - 1] + cost
          );
        }
      }

      return matrix[b.length][a.length];
    };

    const distance = levenshteinDistance(str1, str2);
    const maxLength = Math.max(str1.length, str2.length);
    const similarityPercentage = Math.round((1 - distance / maxLength) * 100);

    return Math.max(0, similarityPercentage);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const playAccuracySound = (accuracy) => {
    let soundFile = '';
    if (accuracy > 80) {
      soundFile = 'xanh.mp3';
    } else if (accuracy > 50) {
      soundFile = 'vang.mp3';
    } else {
      soundFile = 'do.mp3';
    }

    const audio = new Audio(`/sound/${soundFile}`);

    audio.play().catch(error => {
      console.error('Không thể phát âm thanh:', error);
    });
  };

  const splitPhrase = (phrase) => {
    // Handle both string and object formats
    if (typeof phrase === 'string') {
      // Legacy format: "Hello - Xin chào"
      const parts = phrase.split('-').map(part => part.trim());
      return {
        english: parts[0] || '',
        vietnamese: parts.slice(1).join('-') || ''
      };
    } else if (phrase && typeof phrase === 'object') {
      // New API format: {id: 112, english_text: 'Duck', vietnamese_text: ''}
      if (phrase.english_text !== undefined || phrase.vietnamese_text !== undefined) {
        return {
          english: phrase.english_text || '',
          vietnamese: phrase.vietnamese_text || ''
        };
      }

      // Fallback: try other common properties
      const phraseText = phrase.text || phrase.content || phrase.combinedText || phrase.phrase || '';
      if (phraseText) {
        const parts = phraseText.split('-').map(part => part.trim());
        return {
          english: parts[0] || '',
          vietnamese: parts.slice(1).join('-') || ''
        };
      }
    }

    // If nothing found, return empty
    return { english: '', vietnamese: '' };
  };

  // Hàm tính toán phân trang
  const getPaginatedPhrases = () => {
    if (!selectedLesson) return [];

    const indexOfLastPhrase = currentPage * phrasesPerPage;
    const indexOfFirstPhrase = indexOfLastPhrase - phrasesPerPage;
    return selectedLesson.phrases.slice(indexOfFirstPhrase, indexOfLastPhrase);
  };

  // Hàm chuyển trang
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        {/* Hiển thị Login form trực tiếp */}
        {!showRegisterForm ? (
          <Login 
            onLoginSuccess={handleLoginSuccess} 
            onClose={() => {}} 
            onSwitchToRegister={() => setShowRegisterForm(true)}
          />
        ) : (
          <Register 
            onRegisterSuccess={handleRegisterSuccess} 
            onClose={() => setShowRegisterForm(false)}
            onSwitchToLogin={() => setShowRegisterForm(false)}
          />
        )}
      </>
    );
  }

  // Component chính cho app đã đăng nhập
  const MainApp = () => {
    const renderContent = () => {
      // Modal-like views should be checked first
      if (enlargedPhrase) {
        return (
          <PhrasePractice
            enlargedPhrase={enlargedPhrase}
            closeEnlargedView={closeEnlargedView}
            isRecording={isRecording}
            recordingTime={formatTime(recordingTime)}
            audioURL={audioURL}
            transcription={transcription}
            transcriptionFeedback={transcriptionFeedback}
            isTranscribing={isTranscribing}
            accuracy={transcriptionAccuracy}
            startRecording={startRecording}
            stopRecording={stopRecording}
            transcribeAudio={transcribeAudio}
          />
        );
      }

      // Main content views based on the 'view' state
      if (view === 'chatbot') {
        return <Chatbot userId={currentUser?.id} />;
      }

      if (view === 'exercise-generator') {
        return <ExerciseGenerator />;
      }

      if (view === 'hangman') {
        return <HangmanGame />;
      }

      if (view === 'word-scramble') {
        return <WordScrambleGame />;
      }

      if (view === 'drag-drop') {
        return <DragDropGame />;
      }

      // Default to 'lessons' view
      if (selectedLesson) {
        const currentPhrases = getPaginatedPhrases();
        return (
          <LessonDetail
            lesson={selectedLesson}
            onBack={backToLessons}
            onAddPhrase={addPhrase}
            onDeletePhrase={deletePhrase}
            onShowEnlarged={showEnlargedPhrase}
            newPhrase={newPhrase}
            setNewPhrase={setNewPhrase}
            paginatedPhrases={currentPhrases}
            currentPage={currentPage}
            phrasesPerPage={phrasesPerPage}
            paginate={paginate}
            splitPhrase={splitPhrase}
            // Add phrase recording props
            isAddPhraseRecording={isAddPhraseRecording}
            addPhraseAudioURL={addPhraseAudioURL}
            startAddPhraseRecording={startAddPhraseRecording}
            stopAddPhraseRecording={stopAddPhraseRecording}
            transcribeAddPhraseAudio={transcribeAddPhraseAudio}
            isAddPhraseTranscribing={isAddPhraseTranscribing}
            addPhraseRecordingTime={addPhraseRecordingTime}
          />
        );
      } 
      
      return (
        <LessonList
          lessons={lessons}
          isLoading={isLoading}
          error={error}
          onSelectLesson={selectLesson}
          onDeleteLesson={deleteLesson}
          onAddLesson={addLesson}
          newLessonTitle={newLessonTitle}
          setNewLessonTitle={setNewLessonTitle}
          lastAdded={lastAdded}
          getGradient={getGradient}
        />
      );
    };

    return (
      <div className={`min-h-screen font-sans bg-gradient-to-br from-gray-50 to-blue-50 text-gray-900 ${fadeIn ? 'opacity-100' : 'opacity-0'} transition-opacity duration-500`}>
        {/* TV Mode Indicator */}
        {isTVMode && (
          <div className="fixed top-4 left-4 z-50 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center space-x-2 animate-pulse">
            <span className="text-xl">📺</span>
            <span className="font-bold text-sm">TV Mode</span>
          </div>
        )}

        {/* Header với full width */}
        <Header
          view={view}
          setView={setView}
          isAuthenticated={isAuthenticated}
          currentUser={currentUser}
          handleLogout={handleLogout}
          setShowLoginForm={setShowLoginForm}
          setShowRegisterForm={setShowRegisterForm}
        />

        {/* Content với container */}
        <div className="w-full max-w-7xl mx-auto px-4 py-6">
          <main>
            {renderContent()}
          </main>

          <Footer />
        </div>

        {showSuccess && (
          <div className="fixed bottom-5 right-5 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-xl shadow-2xl transition-transform transform animate-bounce z-50 border border-white/20">
            <div className="flex items-center space-x-2">
              <span className="text-lg">✅</span>
              <span className="font-semibold">{showSuccess}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="fixed bottom-5 left-5 bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-3 rounded-xl shadow-2xl transition-transform transform animate-bounce z-50 border border-white/20">
            <div className="flex items-center space-x-2">
              <span className="text-lg">❌</span>
              <span className="font-semibold">{error}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Router>
      <Routes>
        {/* Route admin - chỉ admin mới vào được */}
        <Route 
          path="/admin" 
          element={
            <ProtectedAdminRoute currentUser={currentUser}>
              <AdminPanel />
            </ProtectedAdminRoute>
          } 
        />
        
        {/* Route chính - app học tiếng Anh */}
        <Route path="/*" element={<MainApp />} />
      </Routes>
    </Router>
  );
}

export default EnglishLearningApp;
